import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { NgIf } from '@angular/common';

/**
 * The one LumberLinq loader (2026-10-04): the same look as the page-reload splash in index.html (the logo icon breathing inside a
 * soft glow ring), so every "please wait" in the app looks like the first one the user sees. Use it instead of the bare PrimeNG
 * spinner.
 *
 *   <app-ll-loader size="card" [label]="'Loading...' | t"></app-ll-loader>
 *
 * size: 'card' (default, inside a box or section), 'page' (bigger, for a whole empty screen), 'inline' (small, beside text or
 * inside a button area). Theme tokens only, works in light and dark; the animation stops under prefers-reduced-motion.
 */
@Component({
  selector: 'app-ll-loader',
  standalone: true,
  imports: [NgIf],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="llL" [class.llL--page]="size === 'page'" [class.llL--inline]="size === 'inline'" role="status" aria-live="polite">
      <span class="llL-ring"><i class="llL-arc"></i><svg class="llL-logo" viewBox="0 0 120 120" aria-hidden="true"><g transform="translate(8,6)"><path d="M58.5 4.44A13.51 13.51 0 0 1 62 8.09c7.17 11.38-16.15 53.76-19.83 61.44-1.56 3.29 1.4 5.87 11.29 4.74 27.29-1 36.1 7.56 38.15 16.21a52.19 52.19 0 0 0-33.13-86z" fill="#93C47D"/><path d="M20.3 89.73c-16.92-.45 27.62-48.55 30.57-69.86C53.38 1.75 33.07 0 33.07 0a12.44 12.44 0 0 1 7.66 5.34 52.21 52.21 0 1 0 40.79 94.14c3.8 1.52 7.1 4 7.7 8 1.35-24.64-53.9-17.35-68.92-17.75z" fill="#1684ac"/></g></svg></span>
      <span class="llL-label" *ngIf="label">{{ label }}</span>
    </div>
  `,
  styles: [`
    :host { display: inline-block; }
    .llL { --llL-size: 3.25rem; display: flex; flex-direction: column; align-items: center; gap: 0.9rem; }
    .llL--page { --llL-size: 5rem; gap: 1.1rem; }
    .llL--inline { --llL-size: 1.6rem; flex-direction: row; gap: 0.55rem; }
    .llL-ring {
      width: calc(var(--llL-size) * 1.7); height: calc(var(--llL-size) * 1.7);
      display: flex; align-items: center; justify-content: center; border-radius: 50%;
      background: radial-gradient(circle, color-mix(in srgb, #1684ac 26%, transparent) 0%, transparent 70%); position: relative;
      animation: llLRing 1.8s ease-in-out infinite;
    }
    .llL--inline .llL-ring { width: calc(var(--llL-size) * 1.3); height: calc(var(--llL-size) * 1.3); }
    .llL-logo { width: var(--llL-size); height: var(--llL-size); object-fit: contain; animation: llLSwing 3.4s ease-in-out infinite; }
    /* thin turning arc: says "loading" (logo variant A, approved 2026-10-08) */
    .llL-arc { position: absolute; inset: 3%; border-radius: 50%; padding: 2px; pointer-events: none;
      background: conic-gradient(from 0deg, transparent 0 60%, #1684ac 85%, #1fa39a 100%);
      -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite: xor; mask-composite: exclude;
      animation: llLSpin 1.2s linear infinite; }
    .llL-ring { perspective: 620px; }
    .llL-label { font-size: 0.85rem; font-weight: 600; letter-spacing: 0.03em; color: var(--text-color-secondary, #64748b); }
    .llL--page .llL-label { font-size: 1rem; }
    @keyframes llLSwing { 0%, 100% { transform: rotateY(-22deg) rotateX(5deg); } 50% { transform: rotateY(22deg) rotateX(-4deg); } }
    @keyframes llLSpin { to { transform: rotate(360deg); } }
    @keyframes llLRing { 0%, 100% { transform: scale(0.92); opacity: 0.7; } 50% { transform: scale(1.15); opacity: 1; } }
    @media (prefers-reduced-motion: reduce) { .llL-ring, .llL-logo, .llL-arc { animation: none; } .llL-logo { transform: rotateY(-12deg); } }
  `],
})
export class LlLoaderComponent {
  @Input() size: 'card' | 'page' | 'inline' = 'card';
  @Input() label: string | null = null;
}
